import { useState, useMemo } from 'react';

export interface ContentFilters {
  search: string;
  status: string[];
  clients: number[];
  projects: number[];
  phases: number[];
  tags: string[];
  assignedTo: string[];
  priority: string[];
  dateRange: { start?: string; end?: string; field: 'created' | 'updated' | 'scheduled' } | null;

  // Type-specific filters
  platform?: string[];  // Social, Ads
  contentType?: string[];  // Websites, Design, Email
  seoScoreRange?: { min: number; max: number };
  budgetRange?: { min: number; max: number };
  fileFormat?: string[];  // Design
  emailType?: string[];  // Email
  designType?: string[];  // Design
  adCampaignType?: string[];  // Ads
}

export interface GroupingConfig {
  groupBy: 'none' | 'client' | 'project' | 'phase' | 'status' | 'assignedTo' | 'contentType' | 'date' | 'priority';
  secondaryGroupBy?: 'none' | 'client' | 'project' | 'phase' | 'status' | 'assignedTo' | 'contentType' | 'date' | 'priority';
  sortBy: 'name' | 'date' | 'status' | 'priority';
  sortOrder: 'asc' | 'desc';
}

const defaultFilters: ContentFilters = {
  search: '',
  status: [],
  clients: [],
  projects: [],
  phases: [],
  tags: [],
  assignedTo: [],
  priority: [],
  dateRange: null,
};

const defaultGrouping: GroupingConfig = {
  groupBy: 'none',
  secondaryGroupBy: 'none',
  sortBy: 'date',
  sortOrder: 'desc',
};

export function useContentFilters<T extends Record<string, any>>(
  items: T[],
  contentType: 'social' | 'website' | 'ads' | 'email' | 'design'
) {
  const [filters, setFilters] = useState<ContentFilters>(defaultFilters);
  const [grouping, setGrouping] = useState<GroupingConfig>(defaultGrouping);

  // Update individual filter
  const updateFilter = <K extends keyof ContentFilters>(
    key: K,
    value: ContentFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  // Remove specific filter
  const removeFilter = (key: keyof ContentFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (Array.isArray(newFilters[key])) {
        newFilters[key] = [] as any;
      } else if (key === 'search') {
        newFilters[key] = '';
      } else if (key === 'dateRange') {
        newFilters[key] = null;
      } else {
        newFilters[key] = undefined as any;
      }
      return newFilters;
    });
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status.length > 0) count++;
    if (filters.clients.length > 0) count++;
    if (filters.projects.length > 0) count++;
    if (filters.phases.length > 0) count++;
    if (filters.tags.length > 0) count++;
    if (filters.assignedTo.length > 0) count++;
    if (filters.priority.length > 0) count++;
    if (filters.dateRange) count++;
    if (filters.platform && filters.platform.length > 0) count++;
    if (filters.contentType && filters.contentType.length > 0) count++;
    if (filters.seoScoreRange) count++;
    if (filters.budgetRange) count++;
    if (filters.fileFormat && filters.fileFormat.length > 0) count++;
    if (filters.emailType && filters.emailType.length > 0) count++;
    if (filters.designType && filters.designType.length > 0) count++;
    if (filters.adCampaignType && filters.adCampaignType.length > 0) count++;
    return count;
  }, [filters]);

  // Apply filters to items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesTitle = item.title?.toLowerCase().includes(searchLower);
        const matchesDescription = item.description?.toLowerCase().includes(searchLower);
        const matchesClient = item.client_name?.toLowerCase().includes(searchLower);
        const matchesSubject = item.subject_line?.toLowerCase().includes(searchLower);
        const matchesCaption = item.caption?.toLowerCase().includes(searchLower);

        if (!matchesTitle && !matchesDescription && !matchesClient && !matchesSubject && !matchesCaption) {
          return false;
        }
      }

      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(item.status)) {
        return false;
      }

      // Client filter
      if (filters.clients.length > 0 && !filters.clients.includes(item.client_id)) {
        return false;
      }

      // Project filter
      if (filters.projects.length > 0 && !filters.projects.includes(item.project_id)) {
        return false;
      }

      // Phase filter
      if (filters.phases.length > 0 && !filters.phases.includes(item.phase_id)) {
        return false;
      }

      // Priority filter
      if (filters.priority.length > 0 && !filters.priority.includes(item.priority)) {
        return false;
      }

      // Assigned To filter
      if (filters.assignedTo.length > 0 && !filters.assignedTo.includes(item.assigned_to)) {
        return false;
      }

      // Tags filter
      if (filters.tags.length > 0) {
        const itemTags = Array.isArray(item.tags) ? item.tags : [];
        const hasTag = filters.tags.some(tag => itemTags.includes(tag));
        if (!hasTag) return false;
      }

      // Date range filter
      if (filters.dateRange) {
        const { start, end, field } = filters.dateRange;
        const dateField = field === 'created' ? item.created_at :
                         field === 'updated' ? item.updated_at :
                         item.scheduled_for;

        if (dateField) {
          const itemDate = new Date(dateField);
          if (start && itemDate < new Date(start)) return false;
          if (end && itemDate > new Date(end)) return false;
        }
      }

      // Content-type specific filters
      if (contentType === 'social' || contentType === 'ads') {
        if (filters.platform && filters.platform.length > 0 && !filters.platform.includes(item.platform)) {
          return false;
        }
      }

      if (contentType === 'website') {
        if (filters.contentType && filters.contentType.length > 0 && !filters.contentType.includes(item.content_type)) {
          return false;
        }
        if (filters.seoScoreRange) {
          const { min, max } = filters.seoScoreRange;
          const score = item.seo_score || 0;
          if (score < min || score > max) return false;
        }
      }

      if (contentType === 'email') {
        if (filters.emailType && filters.emailType.length > 0 && !filters.emailType.includes(item.email_type)) {
          return false;
        }
      }

      if (contentType === 'design') {
        if (filters.designType && filters.designType.length > 0 && !filters.designType.includes(item.design_type)) {
          return false;
        }
        if (filters.fileFormat && filters.fileFormat.length > 0 && !filters.fileFormat.includes(item.file_format)) {
          return false;
        }
      }

      if (contentType === 'ads') {
        if (filters.adCampaignType && filters.adCampaignType.length > 0 && !filters.adCampaignType.includes(item.campaign_type)) {
          return false;
        }
        if (filters.budgetRange) {
          const { min, max } = filters.budgetRange;
          const budget = item.budget || 0;
          if (budget < min || budget > max) return false;
        }
      }

      return true;
    });
  }, [items, filters, contentType]);

  // Helper function to get group key for an item
  function getGroupKey(item: T, groupByField: string): string {
    switch (groupByField) {
      case 'client':
        return item.client_name || 'No Client';
      case 'project':
        return item.project_name || 'No Project';
      case 'phase':
        return item.phase_name || 'No Phase';
      case 'status':
        return item.status || 'Unknown';
      case 'assignedTo':
        return item.assigned_to || 'Unassigned';
      case 'contentType':
        return item.content_type || item.design_type || item.email_type || item.platform || 'Other';
      case 'priority':
        return item.priority || 'No Priority';
      case 'date':
        const date = new Date(item.created_at || item.scheduled_for);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      default:
        return 'Ungrouped';
    }
  }

  // Group and sort items
  const groupedItems = useMemo(() => {
    if (grouping.groupBy === 'none') {
      return sortItems(filteredItems, grouping.sortBy, grouping.sortOrder);
    }

    // Check if we have secondary grouping
    const hasSecondaryGrouping = grouping.secondaryGroupBy && grouping.secondaryGroupBy !== 'none';

    if (!hasSecondaryGrouping) {
      // Single-level grouping (existing logic)
      const groups: Record<string, T[]> = {};

      filteredItems.forEach(item => {
        const groupKey = getGroupKey(item, grouping.groupBy);

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
      });

      // Sort items within each group
      Object.keys(groups).forEach(key => {
        groups[key] = sortItems(groups[key], grouping.sortBy, grouping.sortOrder);
      });

      return groups;
    } else {
      // Two-level grouping (primary -> secondary)
      const groups: Record<string, Record<string, T[]>> = {};

      filteredItems.forEach(item => {
        const primaryKey = getGroupKey(item, grouping.groupBy);
        const secondaryKey = getGroupKey(item, grouping.secondaryGroupBy!);

        if (!groups[primaryKey]) {
          groups[primaryKey] = {};
        }
        if (!groups[primaryKey][secondaryKey]) {
          groups[primaryKey][secondaryKey] = [];
        }
        groups[primaryKey][secondaryKey].push(item);
      });

      // Sort items within each secondary group
      Object.keys(groups).forEach(primaryKey => {
        Object.keys(groups[primaryKey]).forEach(secondaryKey => {
          groups[primaryKey][secondaryKey] = sortItems(
            groups[primaryKey][secondaryKey],
            grouping.sortBy,
            grouping.sortOrder
          );
        });
      });

      return groups;
    }
  }, [filteredItems, grouping]);

  // Helper function to sort items
  function sortItems(items: T[], sortBy: string, sortOrder: 'asc' | 'desc'): T[] {
    const sorted = [...items].sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'name':
          compareValue = (a.title || '').localeCompare(b.title || '');
          break;
        case 'date':
          const dateA = new Date(a.created_at || a.scheduled_for || 0).getTime();
          const dateB = new Date(b.created_at || b.scheduled_for || 0).getTime();
          compareValue = dateA - dateB;
          break;
        case 'status':
          const statusOrder = ['draft', 'pending_review', 'approved', 'published', 'rejected'];
          compareValue = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
          break;
        case 'priority':
          const priorityOrder = ['urgent', 'high', 'medium', 'low'];
          compareValue = priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority);
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
  }

  return {
    filters,
    grouping,
    filteredItems,
    groupedItems,
    activeFilterCount,
    updateFilter,
    clearFilters,
    removeFilter,
    setGrouping,
    isGrouped: grouping.groupBy !== 'none',
    hasSecondaryGrouping: !!(grouping.secondaryGroupBy && grouping.secondaryGroupBy !== 'none'),
  };
}
